import { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { useTranslations } from '@/i18n';
import { parseISO, differenceInDays } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { BusinessTripStatus } from '@/hooks/trips/types';

interface TripSummaryCardProps {
  tripStartDate: string;
  tripEndDate: string;
  expenseTotals?: {
    total: number;
    transport: number;
    accommodation: number;
    fuel: number;
    roaming: number;
    daily: number;
  };
  citiesCount?: number;
  status?: BusinessTripStatus;
  collapsible?: boolean;
  defaultOpen?: boolean;
}

interface ExpenseCategory {
  type: 'transport' | 'accommodation' | 'roaming' | 'daily';
  icon: string;
  label: string;
  amount: number;
  bgColor: string;
  borderColor: string;
}

type KnownStatus = Exclude<BusinessTripStatus, 'unknown'>;

const STATUS_BADGE_STYLES: Record<KnownStatus, { className: string; translationKey: string }> = {
  draft: {
    className:
      'rounded border-none bg-[#f1f1f1] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#292929] hover:bg-[#f1f1f1]',
    translationKey: 'statuses.draft',
  },
  pending: {
    className:
      'rounded border-none bg-[rgba(255,193,7,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#f57c00] hover:bg-[rgba(255,193,7,0.08)]',
    translationKey: 'statuses.pending',
  },
  approved: {
    className:
      'rounded border-none bg-[rgba(2,186,60,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#299764] hover:bg-[rgba(2,186,60,0.08)]',
    translationKey: 'statuses.approved',
  },
  declined: {
    className:
      'rounded border-none bg-[rgba(255,112,112,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#e5484d] hover:bg-[rgba(255,112,112,0.08)]',
    translationKey: 'statuses.declined',
  },
  returned_back: {
    className:
      'rounded border-none bg-[rgba(2,128,255,0.06)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#0880ea] hover:bg-[rgba(2,128,255,0.06)]',
    translationKey: 'statuses.returnedBack',
  },
  cancelled: {
    className:
      'rounded border-none bg-[rgba(108,117,125,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#6c757d] hover:bg-[rgba(108,117,125,0.08)]',
    translationKey: 'statuses.cancelled',
  },
  extended: {
    className:
      'rounded border-none bg-[rgba(0,188,212,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#00bcd4] hover:bg-[rgba(0,188,212,0.08)]',
    translationKey: 'statuses.extended',
  },
  shortened: {
    className:
      'rounded border-none bg-[rgba(255,87,34,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#ff5722] hover:bg-[rgba(255,87,34,0.08)]',
    translationKey: 'statuses.shortened',
  },
  finalizing: {
    className:
      'rounded border-none bg-[rgba(156,39,176,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#9c27b0] hover:bg-[rgba(156,39,176,0.08)]',
    translationKey: 'statuses.finalizing',
  },
  finished: {
    className:
      'rounded border-none bg-[rgba(76,175,80,0.08)] px-2 py-1 text-[13px] leading-[20px] font-normal text-[#4caf50] hover:bg-[rgba(76,175,80,0.08)]',
    translationKey: 'statuses.finished',
  },
};

export default function TripSummaryCard({
  tripStartDate,
  tripEndDate,
  expenseTotals,
  citiesCount,
  status,
  collapsible = false,
  defaultOpen = true,
}: TripSummaryCardProps) {
  const t = useTranslations('trips.summary');
  const tTrips = useTranslations('trips');
  const tActivity = useTranslations('trips.activity');
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const totalDays = differenceInDays(parseISO(tripEndDate), parseISO(tripStartDate)) + 1;

  const transportTotal = expenseTotals?.transport ?? 0;
  const accommodationTotal = expenseTotals?.accommodation ?? 0;
  const roamingTotal = expenseTotals?.roaming ?? 0;
  const dailyTotal = expenseTotals?.daily ?? 0;
  const totalExpenses =
    expenseTotals?.total ?? transportTotal + accommodationTotal + roamingTotal + dailyTotal;

  const formatMoney = (value: number) => {
    if (Number.isInteger(value)) {
      return `${value} GEL`;
    }
    return `${value.toFixed(2)} GEL`;
  };

  const expenseCategories: ExpenseCategory[] = [
    {
      type: 'transport',
      icon: '/assets/icons/plane.svg',
      label: t('transport'),
      amount: transportTotal,
      bgColor: 'bg-[rgba(237,40,145,0.04)]',
      borderColor: 'border-[rgba(255,0,159,0.16)]',
    },
    {
      type: 'accommodation',
      icon: '/assets/icons/building.svg',
      label: t('accommodation'),
      amount: accommodationTotal,
      bgColor: 'bg-[rgba(240,78,73,0.04)]',
      borderColor: 'border-[rgba(255,121,121,0.16)]',
    },
    {
      type: 'roaming',
      icon: '/assets/icons/phone-wave.svg',
      label: t('roaming'),
      amount: roamingTotal,
      bgColor: 'bg-[rgba(66,65,154,0.04)]',
      borderColor: 'border-[rgba(71,71,255,0.16)]',
    },
    {
      type: 'daily',
      icon: '/assets/icons/money.svg',
      label: t('dailyExpense'),
      amount: dailyTotal,
      bgColor: 'bg-[rgba(4,119,43,0.04)]',
      borderColor: 'border-[rgba(0,157,63,0.16)]',
    },
  ];

  const renderStatusBadge = () => {
    if (!status || status === 'unknown' || !(status in STATUS_BADGE_STYLES)) {
      return null;
    }

    const { className, translationKey } = STATUS_BADGE_STYLES[status as KnownStatus];
    return <Badge className={className}>{tTrips(translationKey)}</Badge>;
  };

  const contentVisible = collapsible ? isOpen : true;

  return (
    <div className="box-border flex w-full flex-col gap-4 rounded-2xl bg-white p-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
      <div className="flex w-full items-center justify-between">
        <h2 className="text-[1.125rem] leading-7 font-bold text-[#292929]">{t('title')}</h2>
        <div className="flex items-center gap-2">
          {renderStatusBadge()}
          {collapsible && (
            <button
              type="button"
              onClick={() => setIsOpen((prev) => !prev)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#e4e4e4] bg-white text-[#292929] transition-transform duration-200 hover:scale-[0.98]"
              aria-expanded={isOpen}
              aria-label={isOpen ? tActivity('collapse') : tActivity('expand')}
            >
              <ChevronDownIcon
                className={cn('h-4 w-4 transition-transform', !isOpen && 'rotate-180')}
              />
            </button>
          )}
        </div>
      </div>

      {contentVisible && (
        <>
          <div className="grid w-full gap-5 py-3 text-left sm:grid-cols-3">
            <div className="flex flex-col gap-1">
              <div className="text-sm leading-5 text-[#666666]">{t('totalExpenses')}</div>
              <div className="text-xl leading-7 font-medium text-[#18794e]">
                {formatMoney(totalExpenses)}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="text-sm leading-5 text-[#666666]">{t('cities')}</div>
              <div className="text-xl leading-7 font-medium text-[#292929]">{citiesCount ?? 0}</div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="text-sm leading-5 text-[#666666]">{t('daysCount')}</div>
              <div className="text-xl leading-7 font-medium text-[#292929]">
                {totalDays} {t('days')}
              </div>
            </div>
          </div>

          <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-start lg:justify-start lg:gap-5">
            {expenseCategories.map((category) => (
              <div
                key={category.type}
                className={cn(
                  category.bgColor,
                  category.borderColor,
                  'box-border flex items-center gap-3 rounded-2xl border px-4 py-3',
                  'lg:flex-1 lg:flex-col lg:items-center lg:justify-center lg:gap-4 lg:p-5'
                )}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-white p-2 lg:h-12 lg:w-12">
                  <img src={category.icon} alt="" className="h-6 w-6 lg:h-7 lg:w-7" />
                </div>
                <div className="flex min-w-0 flex-1 items-center justify-between gap-3 lg:flex-col lg:items-center lg:gap-3">
                  <div className="flex min-w-0 flex-col text-left lg:items-center lg:text-center">
                    <div className="text-sm leading-5 font-medium text-[#292929] lg:text-base lg:leading-6">
                      {category.label}
                    </div>
                  </div>
                  <div className="text-right text-base leading-6 font-medium text-[#292929] lg:text-2xl lg:leading-8">
                    {formatMoney(category.amount)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
